import {
  useCreateSculptureEditionHistory,
  useDeleteSculptureEditionHistory,
  useEditSculptureEditionHistory,
  type SculptureEditionHistory,
} from "@/api/sculpture-edition-history.api";
import * as AF from "@/components/form-items/AbstractForm";
import {
  CancelButton,
  DeleteButton,
  SaveButton,
  UpdateButton,
} from "@/components/ui/custom-buttons";
import {
  CustomDialogContent,
  CustomDialogFooter,
  CustomDialogTitle,
} from "@/components/ui/custom-dialog";
import { Dialog, DialogClose, DialogHeader } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { toast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = z.object({
  description: z.string(),
  villePays: z.string(),
  dates: z.string(),
  collectionParticuliere: z.boolean(),
});

type SculptureEditionHistoryForm = z.infer<typeof formSchema>;

const emptyValues: SculptureEditionHistoryForm = {
  description: "",
  villePays: "",
  dates: "",
  collectionParticuliere: false,
};

const toFormValues = (
  history: SculptureEditionHistory,
): SculptureEditionHistoryForm => ({
  description: history.description,
  villePays: history.villePays,
  dates: history.dates,
  collectionParticuliere: history.collectionParticuliere,
});

export type CreateOrEditSculptureEditionHistoryProps = {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  sculptureEditionId: string;
  history?: SculptureEditionHistory;
  disabled?: boolean;
};

export const CreateOrEditSculptureEditionHistory = ({
  open,
  onOpenChange,
  sculptureEditionId,
  history,
  disabled = false,
}: CreateOrEditSculptureEditionHistoryProps) => {
  const { t } = useTranslation();
  const isEdit = history !== undefined;

  const creator = useCreateSculptureEditionHistory();
  const updater = useEditSculptureEditionHistory();
  const deleter = useDeleteSculptureEditionHistory();

  const form = useForm<SculptureEditionHistoryForm>({
    defaultValues: isEdit ? toFormValues(history) : emptyValues,
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (open) {
      form.reset(history ? toFormValues(history) : emptyValues);
    }
  }, [open, history, form]);

  const onSubmit = (values: SculptureEditionHistoryForm) => {
    if (isEdit) {
      updater.mutate(
        {
          historyId: history.id,
          sculptureEditionId,
          history: values,
        },
        {
          onSuccess: () => {
            toast({
              title: t("sculpture.history.toast.successUpdate"),
              variant: "success",
            });
            onOpenChange?.(false);
          },
          onError: () => {
            toast({
              title: t("sculpture.history.toast.errorUpdate"),
              variant: "destructive",
            });
          },
        },
      );
      return;
    }

    creator.mutate(
      { sculptureEditionId, ...values },
      {
        onSuccess: () => {
          toast({
            title: t("sculpture.history.toast.successCreate"),
            variant: "success",
          });
          form.reset();
          onOpenChange?.(false);
        },
        onError: () => {
          toast({
            title: t("sculpture.history.toast.errorCreate"),
            variant: "destructive",
          });
        },
      },
    );
  };

  const onDelete = () => {
    if (!isEdit) return;
    deleter.mutate(
      { historyId: history.id, sculptureEditionId },
      {
        onSuccess: () => {
          toast({
            title: t("sculpture.history.toast.successDelete"),
            variant: "success",
          });
          onOpenChange?.(false);
        },
        onError: () => {
          toast({
            title: t("sculpture.history.toast.errorDelete"),
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <CustomDialogContent>
        <Form {...form}>
          <form
            className="flex flex-col"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <DialogHeader>
              <CustomDialogTitle>
                {isEdit
                  ? t("sculpture.history.editTitle")
                  : t("sculpture.history.addTitle")}
              </CustomDialogTitle>
            </DialogHeader>
            <AF.Col className="p-6">
              <AF.Text
                name="description"
                label={t("sculpture.history.description")}
              />
              <AF.Text
                name="villePays"
                label={t("sculpture.history.villePays")}
              />
              <AF.Text name="dates" label={t("sculpture.history.dates")} />
              <AF.Checkbox
                name="collectionParticuliere"
                label={t("sculpture.history.collectionParticuliere")}
              />
              {isEdit && (
                <DeleteButton
                  variant="link"
                  type="button"
                  className="text-red-600 w-fit m-auto"
                  iconProps={{ className: "stroke-red-600" }}
                  onClick={onDelete}
                  disabled={disabled}
                >
                  {t("sculpture.history.deleteAction")}
                </DeleteButton>
              )}
            </AF.Col>
            <CustomDialogFooter>
              <DialogClose asChild>
                <CancelButton className="flex-1" />
              </DialogClose>
              {isEdit ? (
                <UpdateButton
                  type="submit"
                  className="flex-1"
                  disabled={disabled}
                />
              ) : (
                <SaveButton
                  type="submit"
                  className="flex-1"
                  disabled={disabled}
                />
              )}
            </CustomDialogFooter>
          </form>
        </Form>
      </CustomDialogContent>
    </Dialog>
  );
};

export default CreateOrEditSculptureEditionHistory;
